import React, { useState, useEffect } from 'react'

// @material-ui/core components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { makeStyles } from "tss-react/mui";

// core components
import Button from '../../components/CustomButtons/Button.js';
import Footer from '../../components/Footer/Footer.js';
import GridContainer from '../../components/Grid/GridContainer.js';
import GridItem from '../../components/Grid/GridItem.js';
import Header from '../../components/Header/Header.js'
import HeaderLinks from "../../components/Header/HeaderLinks.js";
import Image from 'next/image.js';
import Parallax from "../../components/Parallax/Parallax.js";
import PreFooter from "../../pages-sections/LandingPage-Sections/PreFooter.js";
import Link from 'next/link';

// sections for this page
import ForumsLoader from '../../components/ForumsLoader/ForumsLoader.js';

import styles from "../../styles/jss/nextjs-material-kit/pages/forumsStyle.js";

// Placeholder images
import headerBg from "../../public/img/forums/forumsHeaderImg.png";
import logo from "../../public/img/logo.png";

import {ForumProvider, useForumContext} from "../../contexts/forum.js";
import {useRouter} from "next/router";

import {buildSlug, updateQueryString} from "../../util/url";

const dashboardRoutes = [];
const useStyles = makeStyles()(styles);

function ForumsHomepage(props) {
    const [registerModal, setRegisterModal] = useState(false);
    const { ...rest } = props;
    const { classes, cx } = useStyles();
    const router = useRouter();

    const { forums, isLoadingForums } = useForumContext();
    const [expandedIndex, setExpandedIndex] = useState(-1);

    const handleAccordionChange = (index) => {
        setExpandedIndex(expandedIndex === index ? -1 : index);
    };

    const faqData = [
        {
            question: "How do I create an account?",
            answer: <p className={classes.accordionDetails}>Click <Link href={updateQueryString(router.asPath, "register", null)} shallow={true} className={ classes.linkOverride + " " + classes.transparentButton + " " + classes.invisibleButton}>here</Link> and follow the registration steps. Be sure to complete your profile with as much detail as possible to receive personalised recommendations and make the most of your account.</p>
        },
        {
            question: "How do I ask a question?",
            answer: <p className={classes.accordionDetails}>First, go to our <Link className={classes.linkOverride + " " + classes.transparentButton + " " + classes.invisibleButton} href={"/community/ask-an-apprentice-138"}>Ask an Apprentice</Link> page. Select the industry relevant to your question, then click the 'Ask a question' button (you’ll be prompted to log in if you haven’t already). If you don’t have an account, you can create one <Link href={updateQueryString(router.asPath, "register", null)} shallow={true} className={ classes.linkOverride + " " + classes.transparentButton + " " + classes.invisibleButton}>here</Link>. After logging in, provide a title, description, and relevant tags for your question to help others find it more easily. Once submitted, your question will need to be approved by our team. You’ll receive an email once your post goes live.</p>
        },
        {
            question: "What should I do if I encounter inappropriate content?",
            answer: <p className={classes.accordionDetails}>If you come across inappropriate content, click the three dots at the top right of the comment and select the "Report" option, choosing a reason for reporting. Our team will review it and take appropriate action if necessary.</p>
        },
        {
            question: "Can I contact other members privately?",
            answer: <p className={classes.accordionDetails}>No. To ensure a safe environment for everyone, we only allow public interactions on the platform.</p>
        },
        {
            question: "How long does it take for my question to be approved?",
            answer: <p className={classes.accordionDetails}>We aim to review and approve questions within 24 hours. Once approved, you’ll receive an email notification, and your question will be visible to the community.</p>
        },
        {
            question: "What if I don’t get an answer to my question?",
            answer: <p className={classes.accordionDetails}>While we encourage our mentors to answer as many questions as possible, some may go unanswered due to volume. We recommend adding clear tags and details to your question to increase visibility. You can also try rephrasing and reposting after some time.</p>
        }
    ]

    return (
        <div>
            <Header
                color="transparent"
                routes={dashboardRoutes}
                logo={logo}
                rightLinks={<HeaderLinks />}
                registerModal={registerModal}
                setRegisterModal={setRegisterModal}
                fixed
                changeColorOnScroll={{
                    height: 400,
                    color: "white",
                }}
                {...rest}
            />
            <Parallax image={headerBg.src} forums={true}>
                <div className={classes.titleBannerContainer}>
                    <h5 className={classes.title}>Welcome to the Success at School Community</h5>
                    <p className={classes.subtitle}>Join our community where students, apprentices,
                        and employers share knowledge and experiences. Get valuable insights and
                        support for your education and career journey.</p>
                </div>
            </Parallax>

            {isLoadingForums ? (
                <ForumsLoader />
            ) :
            (
                <GridContainer className={cx(classes.gridContainer + " " + classes.container + " " + classes.gridContainerMarginAdjust)}>
                    {forums?.subCategories.length > 0 && forums?.subCategories.map((card) => {
                        const buttonText = card.name?.toLowerCase() === "community guidelines" ? "Read the guidelines" : "Explore";
                        return (
                            <GridItem key={card.name} xs={12} sm={12} md={6} className={classes.gridItem + " " + classes.homepageGridMobileOverride + " " + classes.gridItemReducePadding}>
                                <div className={classes.card}>
                                <Link className={classes.linkOverride} href={card.linkedId || `/community/${buildSlug(card.name)}-${card.subCatId}`}>
                                <Image priority className={classes.cardImage} src={card.image} fill alt={card.name}></Image>
                                <h3 className={classes.cardTitle}>{card.name}</h3>
                                <p className={classes.cardText}>{card.description}</p>
                                </Link>
                                <Button className={cx(classes.darkButton + " " + classes.forumsButton)} href={card.linkedId || `/community/${buildSlug(card.name)}-${card.subCatId}`}>{buttonText}</Button>
                                </div>
                            </GridItem>)
                    })}
                </GridContainer>
            )}
            <div className={classes.titleBannerContainer}>
                <h5 className={classes.sectionTitle}>FAQs</h5>
                <p className={classes.sectionSubtitle}>Find answers to common questions about our community,
                    how to participate, and tips for making the most of your experience here.</p>
            </div>
            {faqData.length > 0 && faqData.map((faq, index) => {
                return (
                    <Accordion className={classes.accordion} key={index} expanded={expandedIndex === index} onChange={(e) => handleAccordionChange(index)}>
                        <AccordionSummary className={classes.accordionSummary}>
                            <h5 className={classes.accordionTitle}>{faq.question}</h5>
                            <p className={classes.accordionIcon}>{expandedIndex === index ? "-" : "+"}</p>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                            {faq.answer}
                        </AccordionDetails>
                    </Accordion>
                )
            })}
            <div className={cx(classes.darkBlue, classes.workSection)}>
                <PreFooter />
            </div>
            <div className={classes.footer}>
                <Footer />
            </div>
        </div>
    )
}

export default function ForumsHomepageProviderPage(props) {
  const router = useRouter();
  const { communityId } = router.query;

  return (
    <ForumProvider forumId={communityId}>
      <ForumsHomepage {...props} />
    </ForumProvider>
  );
}
